import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { CardACHSelection, CardACHSelectionView, MIN_ACH_AMOUNT } from '@frontend/payments-card-on-file';
import { usePrintReceipt } from '@frontend/payments-hooks';
import { PaymentsFlowType, StripeElementsWrapper } from '@frontend/payments-stripe-wrapper';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  ContentLoader,
  EmailField,
  Heading,
  useControlledField,
  useFormField,
} from '@frontend/design-system';
import { useInvoicePerson } from '../../../../hooks/use-invoice-person';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useUpdateRequestStatusPoll } from '../../../hooks/use-update-request-status-poll';
import { useFinalizeStepTrackingCallbacks, useInitiateStepTracking } from '../../../utils/step-tracking-store';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CardOnFileNavigationSwitch } from './card-on-file-navigation-switch';
import { useCardOnFilePayment } from './card-on-file-stripe/use-card-on-file';
import { CardOnFileStepBodyProps } from './card-on-file-types';

const DATA_TRACKING_PREFIX = `pay-client-virtualterminal`;

export const CardOnFileStripe = () => {
  const { invoice } = useCollectPaymentMultiContext();
  return (
    <StripeElementsWrapper type={PaymentsFlowType.CardOnFilePayment} locationId={invoice?.locationId}>
      <CardOnFileNavigationSwitch body={CardOnFileStripeContent} />
    </StripeElementsWrapper>
  );
};

const styles = {
  body: css`
    .add-ach-form,
    .add-card-form {
      flex-grow: 1;
      height: 100%;
      padding: 0;

      &__footer {
        margin-top: auto;
      }
    }
  `,
  emailField: css`
    margin-top: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(2)};
  `,
  printReceiptField: css`
    margin-top: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(1)};
  `,
  cardACHContent: css`
    padding: 0 !important;
    margin-bottom: ${theme.spacing(2)};
  `,
};

const CardOnFileStripeContent = ({ onPaymentSuccess, onCompleteStep, onGoBack }: CardOnFileStepBodyProps) => {
  const { t } = useTranslation('payments');

  const [selectedPM, setSelectedPM] = useState<PaymentMethod>();
  const [isReceiptEmail, setIsReceiptEmail] = useState(false);

  const [activeView, setActiveView] = useState<CardACHSelectionView>('payment-methods');

  const {
    invoice,
    invoiceQuery: { refetch: refetchInvoice },
    stripeData,
    activeGateway,
    flow,
  } = useCollectPaymentMultiContext();

  const { personEmail } = useInvoicePerson(invoice);
  const [emailReceipt, setEmailReceipt] = useState<string | undefined>(personEmail);
  const { value: receiptEmail, ...receiptEmailProps } = useControlledField({
    required: false,
    value: emailReceipt,
    type: 'email',
    onChange: (value) => {
      setEmailReceipt(value);
    },
  });

  useEffect(() => {
    setEmailReceipt(personEmail);
  }, [personEmail]);

  const printReceiptField = useFormField({
    type: 'checkbox',
    value: false,
  });
  const shouldPrintReceipt = !!printReceiptField.value;
  const printReceipt = usePrintReceipt();

  const { completeStepTracking, cancelStepTracking } = useFinalizeStepTrackingCallbacks({
    step: 'card-on-file',
    variant: 'card-on-file:complete-payment',
  });

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoice().then((res) => res.data);
  }, [refetchInvoice]);

  const { makeCardOnFilePayment, makingCardOnFilePayment } = useCardOnFilePayment({
    paymentMethod: selectedPM,
    email: personEmail,
    receiptEmail,
    invoice,
    onInvoiceAlreadyPaid: onGoBack,
    onRefetchInvoice,
  });

  const { setProcessingAndStartPoll } = useUpdateRequestStatusPoll(invoice, (updatedInvoice) => {
    onPaymentSuccess();
    if (shouldPrintReceipt) {
      printReceipt(updatedInvoice);
    }
  });

  const makePayment = useCallback(async () => {
    const workflowId = await makeCardOnFilePayment();

    /**
     * If card is declided, or failure happens, workflowId will be undefined.
     */
    if (workflowId) {
      setProcessingAndStartPoll();

      onCompleteStep();

      completeStepTracking();
    }
  }, [makeCardOnFilePayment, onCompleteStep, setProcessingAndStartPoll, completeStepTracking]);

  const goBack = useCallback(() => {
    onGoBack();
    cancelStepTracking();
  }, [onGoBack, cancelStepTracking]);

  const onPrimaryClick = useCallback(() => {
    setIsReceiptEmail(true);
  }, []);

  const onReceiptStepBack = useCallback(() => {
    setIsReceiptEmail(false);
  }, []);

  useInitiateStepTracking({
    activeGateway: 'stripe',
    flow,
    step: 'card-on-file',
    screenWidth: window.innerWidth,
    variant: 'card-on-file:complete-payment',
  });

  const { backTrackingId, completePaymentTrackingId } = useScopedTrackingIds({
    backTrackingId: `${PAYMENT_MULTI_STEPS.cardOnFile}--${activeGateway}--back-btn`,
    completePaymentTrackingId: `${PAYMENT_MULTI_STEPS.cardOnFile}--${activeGateway}--primary-btn`,
  });

  const isPaymentMethodsView = activeView === 'payment-methods';

  const label = useMemo(() => {
    return stripeData.enableACHOnFile
      ? PAYMENT_MULTI_STEP_LABELS['card-and-ach-on-file']
      : PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.cardOnFile];
  }, [stripeData.enableACHOnFile]);

  if (isReceiptEmail) {
    return (
      <StyledStep
        css={styles.body}
        id={PAYMENT_MULTI_STEPS.cardOnFile}
        label={label}
        isValid={!!selectedPM && !receiptEmailProps.error}
        header={{ title: label }}
        footer={{
          onClickBack: onReceiptStepBack,
          onClickPrimary: makePayment,
          back: {
            trackingId: backTrackingId,
          },
          primary: {
            children: shouldPrintReceipt ? t('Complete Payment & Print Receipt') : t('Complete Payment'),
            disabled: !selectedPM || makingCardOnFilePayment || !!receiptEmailProps.error,
            trackingId: completePaymentTrackingId,
          },
        }}
      >
        <ContentLoader
          show={makingCardOnFilePayment}
          message={
            selectedPM?.type === 'us_bank_account'
              ? t('Making ACH on File Payment...')
              : t('Making Card on File Payment...')
          }
        />

        <form onSubmit={(e) => e.preventDefault()}>
          <Heading level={2}>{t('Email Receipt (Optional)')}</Heading>
          <EmailField
            {...receiptEmailProps}
            actionText={t('Clear')}
            css={styles.emailField}
            data-trackingid={`${DATA_TRACKING_PREFIX}-txt-card-on-file-email-receipt`}
            label={t('Email')}
            name='email'
            onActionClick={emailReceipt ? () => setEmailReceipt('') : undefined}
            value={receiptEmail}
          />
          <CheckboxField
            {...printReceiptField}
            css={styles.printReceiptField}
            data-trackingid={`${DATA_TRACKING_PREFIX}-chk-card-on-file-print-receipt`}
            label={t('Print Receipt')}
            name='print-receipt'
          />
        </form>
      </StyledStep>
    );
  }

  return (
    <StyledStep
      css={styles.body}
      id={PAYMENT_MULTI_STEPS.cardOnFile}
      label={label}
      isValid={!!selectedPM}
      header={{ title: label }}
      hasInternalFooter={!isPaymentMethodsView}
      footer={{
        onClickBack: goBack,
        onClickPrimary: onPrimaryClick,
        back: {
          trackingId: backTrackingId,
        },
        primary: {
          children: t('Continue'),
          disabled: !selectedPM || makingCardOnFilePayment,
          trackingId: completePaymentTrackingId,
        },
      }}
      components={{
        Footer: isPaymentMethodsView ? undefined : () => null,
      }}
    >
      <ContentLoader
        show={makingCardOnFilePayment}
        message={
          selectedPM?.type === 'us_bank_account'
            ? t('Making ACH on File Payment...')
            : t('Making Card on File Payment...')
        }
      />

      <CardACHSelection
        locationId={invoice?.locationId}
        patientId={invoice?.person.id}
        /*
         * Prioritizing the invoice.person.email as this is an 'authorization email',
         * and previously what we were doing  */
        patientEmail={invoice?.person.emailAddress ?? personEmail ?? ''}
        selectedPM={selectedPM}
        onChangePM={setSelectedPM}
        paymentAmount={invoice?.billedAmount}
        minACHAmount={MIN_ACH_AMOUNT}
        addCardContent={<CardACHSelection.AddCardForm />}
        addACHContent={<CardACHSelection.AddACHForm />}
        onChangeView={setActiveView}
      >
        <CardACHSelection.Content css={styles.cardACHContent} />
      </CardACHSelection>
    </StyledStep>
  );
};
